// ----------------------------------------------------------------------------
// BidLedger
// ----------------------------------------------------------------------------
// Interactive data table with three live filters:
//   1) Free-text search across title / id / location
//   2) Agency dropdown filter
//   3) Platform dropdown filter
// Selecting a row bubbles the row up to the parent so it can open the drawer.
// ----------------------------------------------------------------------------

import React, { useMemo, useState } from "react";
import { Search, ChevronDown, MapPin, Calendar } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Input } from "./ui/input";
import { LEDGER } from "../constants/testIds";
import { AGENCY_OPTIONS, PLATFORM_OPTIONS } from "../data/mockData";

// Pretty-format the closing-date string (YYYY-MM-DD -> "Jun 15, 2026").
const formatDate = (iso) => {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const valueTierStyle = (tier) => {
  if (tier === "High-Value") return "bg-indigo-50 text-indigo-700 border-indigo-100";
  if (tier === "Mid-Tier") return "bg-sky-50 text-sky-700 border-sky-100";
  return "bg-slate-50 text-slate-600 border-slate-200";
};

export const BidLedger = ({ bids, onRowClick }) => {
  const [query, setQuery] = useState("");
  const [agency, setAgency] = useState("All Agencies");
  const [platform, setPlatform] = useState("All Platforms");

  // Derived filtered list — recomputed only when inputs change.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return bids.filter((b) => {
      const matchesQuery =
        !q ||
        b.title.toLowerCase().includes(q) ||
        b.id.toLowerCase().includes(q) ||
        b.location.toLowerCase().includes(q);
      const matchesAgency = agency === "All Agencies" || b.agency === agency;
      const matchesPlatform =
        platform === "All Platforms" || b.platform === platform;
      return matchesQuery && matchesAgency && matchesPlatform;
    });
  }, [bids, query, agency, platform]);

  return (
    <section className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
      {/* Toolbar — search + filter dropdowns */}
      <div className="p-4 sm:p-5 border-b border-slate-200 flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="relative flex-1">
          <Search
            size={16}
            strokeWidth={1.7}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
          />
          <Input
            data-testid={LEDGER.search}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by project title, tracking ID, or location…"
            className="pl-9 h-10 bg-slate-50/60 border-slate-200 focus-visible:ring-sky-500/30"
          />
        </div>

        <div className="flex flex-col sm:flex-row gap-3 lg:gap-3">
          <Select value={agency} onValueChange={setAgency}>
            <SelectTrigger
              data-testid={LEDGER.agencyFilter}
              className="w-full sm:w-[210px] h-10 bg-white border-slate-200"
            >
              <SelectValue placeholder="Filter by agency" />
            </SelectTrigger>
            <SelectContent>
              {AGENCY_OPTIONS.map((a) => (
                <SelectItem key={a} value={a} data-testid={`agency-opt-${a}`}>
                  {a}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={platform} onValueChange={setPlatform}>
            <SelectTrigger
              data-testid={LEDGER.platformFilter}
              className="w-full sm:w-[200px] h-10 bg-white border-slate-200"
            >
              <SelectValue placeholder="Filter by platform" />
            </SelectTrigger>
            <SelectContent>
              {PLATFORM_OPTIONS.map((p) => (
                <SelectItem key={p} value={p} data-testid={`platform-opt-${p}`}>
                  {p}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto thin-scroll">
        <table className="w-full text-sm">
          <thead className="bg-slate-50/80 border-b border-slate-200">
            <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-slate-500">
              <th className="px-5 py-3 font-semibold w-[110px]">Status</th>
              <th className="px-5 py-3 font-semibold">Project</th>
              <th className="px-5 py-3 font-semibold whitespace-nowrap">
                Tracking ID
              </th>
              <th className="px-5 py-3 font-semibold">Agency</th>
              <th className="px-5 py-3 font-semibold">Platform</th>
              <th className="px-5 py-3 font-semibold">Value Tier</th>
              <th className="px-5 py-3 font-semibold whitespace-nowrap">
                Closing
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td
                  colSpan={7}
                  data-testid={LEDGER.empty}
                  className="px-5 py-12 text-center text-slate-500"
                >
                  No bids match your filters.
                </td>
              </tr>
            )}
            {filtered.map((b) => (
              <tr
                key={b.id}
                data-testid={LEDGER.row(b.id)}
                onClick={() => onRowClick(b)}
                className="border-b border-slate-100 hover:bg-slate-50/70 cursor-pointer transition-colors"
              >
                <td className="px-5 py-4">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-emerald-100 text-emerald-800 border border-emerald-200">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    Active
                  </span>
                </td>
                <td className="px-5 py-4">
                  <div className="font-medium text-slate-900 leading-snug">
                    {b.title}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
                    <MapPin size={11} strokeWidth={1.7} /> {b.location}
                  </div>
                </td>
                <td className="px-5 py-4">
                  <code className="font-mono text-[12.5px] text-slate-700 bg-slate-100/70 px-1.5 py-0.5 rounded">
                    {b.id}
                  </code>
                </td>
                <td className="px-5 py-4 text-slate-700">{b.agency}</td>
                <td className="px-5 py-4">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-slate-100 text-slate-700">
                    {b.platform}
                  </span>
                </td>
                <td className="px-5 py-4">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${valueTierStyle(
                      b.value,
                    )}`}
                  >
                    {b.value}
                  </span>
                </td>
                <td className="px-5 py-4 text-slate-700 whitespace-nowrap">
                  <div className="flex items-center gap-1.5">
                    <Calendar size={13} strokeWidth={1.7} className="text-slate-400" />
                    {formatDate(b.closing)}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="px-5 py-3 border-t border-slate-200 bg-slate-50/60 text-xs text-slate-500 flex items-center justify-between">
        <span>
          Showing <span className="font-semibold text-slate-700">{filtered.length}</span>{" "}
          of <span className="font-semibold text-slate-700">{bids.length}</span> active bids
        </span>
        <span className="hidden sm:inline-flex items-center gap-1">
          Sorted by closing date <ChevronDown size={12} strokeWidth={1.8} />
        </span>
      </div>
    </section>
  );
};
